import { useState, useEffect } from 'react';
import { isBefore, startOfDay, isSameDay } from "date-fns";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Booking } from "@/hooks/useBookings";
import { parseDateFromDatabase } from "@/utils/dateParser";
import { useSupabaseAvailability } from "@/hooks/useSupabaseAvailability";
import CalendarDayOptionsModal from "./CalendarDayOptionsModal";

// Mapeo de colores por origen
const ORIGIN_COLORS: Record<string, string> = {
  airbnb: "bg-[#fec9cb] hover:bg-red-500",
  "airbnb.com": "bg-[#fec9cb] hover:bg-red-500",
  booking: "bg-[#6FA4F2] hover:bg-blue-600",
  "booking.com": "bg-[#6FA4F2] hover:bg-blue-600",
  "estancia corta": "bg-blue-600 hover:bg-blue-700 text-white",
  "reservas directas": "bg-[#B3FDDC] hover:bg-teal-500",
  directo: "bg-[#B3FDDC] hover:bg-teal-500",
  "redes sociales": "bg-green-500 hover:bg-green-600",
  "referencia": "bg-purple-300 hover:bg-purple-400 text-purple-900",
  "otro": "bg-gray-400 hover:bg-gray-500",
  default: "bg-gray-300 hover:bg-gray-400 text-gray-900"
};

interface Property {
  id: string;
  title: string;
  shortName?: string;
}

interface CalendarGridProps {
  days: Date[];
  properties: Property[];
  bookingsByProperty: { [key: string]: Booking[] };
  isDayBooked: (day: Date, propertyId: string) => boolean;
  isDateBlocked: (date: Date, propertyId: string) => boolean;
  today: Date;
  cellWidth: number;
  rowHeight: number;
  onDayClick: (day: Date, property: Property) => void;
  onBookingClick?: (booking: Booking) => void;
  isAdminContext?: boolean;
  onDateUpdated?: () => void;
  onAddReservation?: (date: Date, property: Property) => void;
  onBlockDate?: (date: Date, property: Property) => void;
}

const CalendarGrid = ({
  days,
  properties,
  bookingsByProperty,
  isDayBooked,
  isDateBlocked: parentIsDateBlocked,
  today,
  cellWidth,
  rowHeight,
  onDayClick,
  onBookingClick,
  isAdminContext = false,
  onDateUpdated,
  onAddReservation,
  onBlockDate,
}: CalendarGridProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [showDayOptionsModal, setShowDayOptionsModal] = useState(false);

  console.log('🔧 CalendarGrid render (3 months):', { 
    isAdminContext, 
    propertiesCount: properties.length,
    daysCount: days.length
  });

  // Hook para obtener fechas bloqueadas manualmente SOLO de property_blocked_days
  const propertyAvailabilityData: { [key: string]: ReturnType<typeof useSupabaseAvailability> } = {};
  
  properties.forEach(property => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    propertyAvailabilityData[property.id] = useSupabaseAvailability(property.id);
  });

  // Función CORREGIDA: Solo considerar fechas bloqueadas manualmente
  const isDateBlockedManually = (date: Date, propertyId: string) => {
    const propertyData = propertyAvailabilityData[propertyId];
    if (!propertyData || propertyData.isLoading) return false;
    
    const normalizeDate = (d: Date) => {
      const normalized = new Date(d);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    };
    
    const normalizedCheckDate = normalizeDate(date);
    const isBlocked = propertyData.unavailableDates.some(blockedDate => {
      const normalizedBlockedDate = normalizeDate(blockedDate);
      return normalizedCheckDate.getTime() === normalizedBlockedDate.getTime();
    });
    
    console.log(`🔍 CalendarGrid: Checking manual block for ${date.toDateString()} in property ${propertyId}: ${isBlocked}`);
    
    return isBlocked;
  };

  const handleDayClick = (day: Date, property: Property) => {
    
    const isBlocked = isDateBlockedManually(day, property.id);
    const isPast = isBefore(day, startOfDay(today));
    const isBooked = isDayBooked(day, property.id);
    
    // En contexto de admin, permitir clicks en cualquier fecha
    // En contexto público, solo permitir clicks en fechas futuras no bloqueadas
    const canClick = isAdminContext ? true : (!isPast && !isBlocked && !isBooked);
    
    if (canClick) {
      setSelectedDate(day);
      setSelectedProperty(property);
      setShowDayOptionsModal(true);
      
      // También llamar al handler original si existe
      if (onDayClick) {
        onDayClick(day, property);
      }
    }
  };

  const handleModalClose = () => {
    setShowDayOptionsModal(false);
    setSelectedDate(null);
    setSelectedProperty(null);
  };

  const handleAddReservation = () => {
    if (selectedDate && selectedProperty && onAddReservation) {
      onAddReservation(selectedDate, selectedProperty);
      handleModalClose();
    }
  };

  const handleBlockDate = () => {
    if (selectedDate && selectedProperty) {
      if (onBlockDate) {
        onBlockDate(selectedDate, selectedProperty);
      }
      handleModalClose();
    }
  };

  return (
    <>
      <div className="divide-y bg-white">
        {properties.map((property, propertyIndex) => (
          <div
            key={property.id}
            className="flex relative border-b"
            style={{ height: rowHeight, minHeight: rowHeight, maxHeight: rowHeight }}
          >
            {days.map((day, dayIndex) => {
              const isBlocked = isDateBlockedManually(day, property.id);
              const isPast = isBefore(day, startOfDay(today));
              const isBooked = isDayBooked(day, property.id);
              
              // En contexto de admin, permitir clicks en cualquier fecha
              // En contexto público, solo permitir clicks en fechas futuras no bloqueadas
              const canClick = isAdminContext ? true : (!isPast && !isBlocked && !isBooked);
              
              let cellBgClass = "";
              // CORRECCIÓN: Eliminar el color azul para fechas reservadas
              if (isBlocked) {
                cellBgClass = "bg-gray-600"; // Solo fechas bloqueadas manualmente
              } else if (isPast && !isAdminContext) {
                cellBgClass = "bg-gray-100";
              } else if (isPast && isAdminContext) {
                cellBgClass = "bg-gray-50";
              }
              // Eliminado: } else if (isBooked) { cellBgClass = "bg-blue-100"; 
              
              const hoverClass = canClick ? "hover:bg-blue-50 cursor-pointer" : "cursor-not-allowed";
              
              return (
                <div
                  key={`${property.id}-${day.toISOString()}`}
                  style={{ 
                    width: cellWidth,
                    minWidth: cellWidth,
                    maxWidth: cellWidth,
                    height: rowHeight,
                    minHeight: rowHeight,
                    maxHeight: rowHeight
                  }}
                  className={`border-r flex flex-col items-center justify-center relative ${cellBgClass} ${hoverClass}`}
                  onClick={() => handleDayClick(day, property)}
                >
                  {/* Línea diagonal SOLO para fechas bloqueadas manualmente (NO reservaciones) */}
                  {isBlocked && !isBooked && (
                    <div 
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: `linear-gradient(45deg, transparent 45%, #374151 45%, #374151 55%, transparent 55%)`
                      }}
                    />
                  )}
                </div>
              );
            })}
            
            {(bookingsByProperty[property.id] || []).map(booking => {
              // CORRECCIÓN CRÍTICA: Usar parseDateFromDatabase para respetar fechas exactas de DB
              const checkIn = parseDateFromDatabase(booking.check_in);
              const checkOut = parseDateFromDatabase(booking.check_out);
              
              if (!checkIn) {
                console.warn(`❌ CalendarGrid: Invalid check-in date for booking ${booking.id}: ${booking.check_in}`);
                return null;
              }
              
              const startIndex = days.findIndex(day => isSameDay(day, checkIn));
              const endIndex = checkOut ? days.findIndex(day => isSameDay(day, checkOut)) : -1;
              
              if (startIndex === -1) {
                console.warn(`❌ CalendarGrid: Check-in date ${checkIn.toDateString()} not found in calendar days for booking ${booking.id}`);
                return null;
              }

              // Calcular la posición visual de la reservación
              const barLeft = startIndex * cellWidth + cellWidth / 2;
              const barEndPosition = endIndex !== -1 
                ? endIndex * cellWidth + cellWidth / 2 
                : (startIndex + booking.nights) * cellWidth + cellWidth / 2;
              const barWidth = barEndPosition - barLeft;

              const originNameRaw = (booking.origins && booking.origins.name) || "";
              const originName = originNameRaw.trim().toLowerCase();

              const colorClass = ORIGIN_COLORS[originName] ||
                (originName.includes("airbnb") ? ORIGIN_COLORS["airbnb"] :
                 originName.includes("booking") ? ORIGIN_COLORS["booking"] :
                 ORIGIN_COLORS["default"]);

              // Verificar si la reservación tiene nota
              const hasNote = booking.note && booking.note.trim().length > 0;

              return (
                <Tooltip key={booking.id}>
                  <TooltipTrigger asChild>
                    <div
                      className={`absolute z-10 top-1/2 -translate-y-1/2 rounded ${colorClass} text-black flex items-center px-2 cursor-pointer`}
                      style={{
                        width: barWidth,
                        left: `${barLeft}px`,
                        height: rowHeight - 14,
                        fontSize: 10,
                        borderRadius: 24,
                      }}
                      title={`${booking.guest_name} – ${booking.check_in} a ${booking.check_out}`}
                      onClick={e => {
                        e.stopPropagation();
                        if (onBookingClick) onBookingClick(booking);
                      }}
                    >
                      {/* Círculo indicador de nota */}
                      {hasNote && (
                        <div 
                          className="w-2 h-2 bg-gray-600 rounded-full mr-1 flex-shrink-0"
                          title="Esta reservación tiene notas"
                        />
                      )}
                      <p className="truncate font-medium">{booking.guest_name}</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{booking.guest_name}</p>
                    <p>Check-in: {booking.check_in}</p>
                    <p>Check-out: {booking.check_out}</p>
                    <p>Noches: {booking.nights}</p>
                    {originNameRaw && (
                      <p>
                        Origen:{" "}
                        <span className="capitalize">{originNameRaw}</span>
                      </p>
                    )}
                    {hasNote && (
                      <div className="mt-2 pt-2 border-t">
                        <p className="font-semibold">Nota:</p>
                        <p className="text-sm max-w-xs whitespace-pre-wrap">{booking.note}</p>
                      </div>
                    )}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        ))}
      </div>

      {/* Modal de opciones del día */}
      {selectedDate && selectedProperty && (
        <CalendarDayOptionsModal
          isOpen={showDayOptionsModal}
          onClose={handleModalClose}
          selectedDate={selectedDate}
          selectedProperty={selectedProperty}
          isDateBlocked={isDateBlockedManually(selectedDate, selectedProperty.id)}
          onAddReservation={handleAddReservation}
          onBlockDate={handleBlockDate}
        />
      )}
    </>
  );
};

export default CalendarGrid;
